import { useCallback, useSyncExternalStore } from 'react';

function mql(query: string): MediaQueryList | null {
  try {
    return window.matchMedia(query);
  } catch {
    return null;
  }
}

/** Live `matchMedia(query).matches` (false when matchMedia is unavailable). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = mql(query);
      if (!list) return () => undefined;
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const get = () => mql(query)?.matches ?? false;
  return useSyncExternalStore(subscribe, get, () => false);
}

export type LayoutMode = 'desktop' | 'tablet' | 'mobile';

/** Layout breakpoints (UX-SPEC §3). */
export const DESKTOP_MIN_WIDTH = 1180;
export const TABLET_MIN_WIDTH = 760;
/**
 * The tablet layout (top bar over a board + side column) needs this much
 * height. A phone turned sideways (e.g. 844×390) is tablet-wide but far too
 * short for it, so it gets the mobile layout (two columns in landscape).
 */
export const TABLET_MIN_HEIGHT = 560;

const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;
const TABLET_QUERY = `(min-width: ${TABLET_MIN_WIDTH}px) and (min-height: ${TABLET_MIN_HEIGHT}px)`;

/**
 * desktop ≥ 1180 wide; tablet 760–1179 wide AND ≥ 560 tall; everything else
 * (phones, and phones in landscape) is mobile. `useLayout` is the live version.
 */
export function layoutFor(width: number, height: number): LayoutMode {
  if (width >= DESKTOP_MIN_WIDTH) return 'desktop';
  if (width >= TABLET_MIN_WIDTH && height >= TABLET_MIN_HEIGHT) return 'tablet';
  return 'mobile';
}

export function useLayout(): LayoutMode {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const tablet = useMediaQuery(TABLET_QUERY);
  return desktop ? 'desktop' : tablet ? 'tablet' : 'mobile';
}

export function useReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}
